import ApplicationController from 'javascript/controllers/application_controller';
import { trackGa4Event } from 'javascript/utils/utils';
import * as TicketCartStorage from 'javascript/utils/ticket_cart_storage';

export default class extends ApplicationController {
  static targets = [
    'continueForm',
    'orderForm',
    'cart',
    'cartDrawer',
    'cartTicketCostDisplay',
    'cartCountDisplay',
    'cartCountWordsDisplay',
    'cartFeeDisplay',
    'cartTotalDisplay',
    'maxErrorMessage',
    'ticketType',
    'seatingInfo',
    'checkoutButton',
  ];

  get maxOrderLimit() {
    return this.data.has('maxOrderLimit')
      ? parseInt(this.data.get('maxOrderLimit'), 10)
      : null;
  }

  get eventProtectPercentage() {
    return this.data.has('eventProtectPercentage')
      ? parseFloat(this.data.get('eventProtectPercentage'))
      : 0;
  }

  get totalTicketFees() {
    return this.sumTicketSelectorAttribute('Fees');
  }

  get allSavedTickets() {
    return TicketCartStorage.getTicketsWithQuantity(this.eventSlug);
  }

  get totalTicketCost() {
    return this.sumTicketSelectorAttribute('TicketCost');
  }

  get totalCost() {
    return this.sumTicketSelectorAttribute('Cost');
  }

  get totalQuantity() {
    return TicketCartStorage.totalQuantity(this.eventSlug);
  }

  get totalFees() {
    return this.totalTicketFees + this.totalEventProtectFees;
  }

  get grandTotal() {
    return this.totalTicketCost + this.totalFees;
  }

  get totalEventProtectFees() {
    return this.totalCost * (this.eventProtectPercentage / 100.0);
  }

  get maxOrderLimitReached() {
    if (this.maxOrderLimit === null) return false;
    return this.totalQuantity >= this.maxOrderLimit;
  }

  get eventName() {
    return this.data.get('eventName');
  }

  get eventSlug() {
    return (
      this.data.get('eventSlug') ||
      window.persistedEventSlug ||
      window.eventSlug ||
      this.eventSlugFromURL()
    );
  }

  eventSlugFromURL() {
    const pathSegments = window.location.pathname.split('/');
    if (pathSegments.length >= 3 && pathSegments[1] === 'events') {
      return pathSegments[2];
    }
    return null;
  }

  get alreadyHasTickets() {
    return this.data.get('alreadyHasTickets') === 'true';
  }

  connect() {
    this.seedEventTicketIdsFromPage();
    this.bindTurboListeners();

    if (this.eventSlug) {
      window.eventSlug = this.eventSlug;
      window.persistedEventSlug = this.eventSlug;
    }

    this.document.addEventListener(
      'turbo:before-cache',
      this.removeHtmlClasses,
      false,
    );
    this.window.addEventListener(
      'DOMContentLoaded',
      this.removeElementContentLoading,
    );
    this.currencySymbol = this.data.get('event-currency-symbol');
    this.ticketCounterTranslationsHash = JSON.parse(
      this.data.get('ticket-counter-translations'),
    );

    this.renderCartFromStorage();
    this.trackViewItemListEvent();
  }

  disconnect() {
    this.unbindTurboListeners();
    this.removeHtmlClasses();
    this.document.removeEventListener(
      'turbo:before-cache',
      this.removeHtmlClasses,
    );
  }

  bindTurboListeners() {
    document.addEventListener('turbo:frame-load', this.onTicketsFrameEvent);
    document.addEventListener('turbo:frame-render', this.onTicketsFrameEvent);
    document.addEventListener(
      'turbo:before-frame-render',
      this.beforeTicketsFrameRender,
    );
    document.addEventListener(
      'turbo:before-fetch-request',
      this.beforeTicketsFrameFetch,
    );
    document.addEventListener('turbo:load', this.onTurboLoad);
  }

  unbindTurboListeners() {
    document.removeEventListener('turbo:frame-load', this.onTicketsFrameEvent);
    document.removeEventListener(
      'turbo:frame-render',
      this.onTicketsFrameEvent,
    );
    document.removeEventListener(
      'turbo:before-frame-render',
      this.beforeTicketsFrameRender,
    );
    document.removeEventListener(
      'turbo:before-fetch-request',
      this.beforeTicketsFrameFetch,
    );
    document.removeEventListener('turbo:load', this.onTurboLoad);
  }

  onTurboLoad = () => {
    if (!this.isOnTicketSelectionPage()) return;

    this.seedEventTicketIdsFromPage();
    this.renderCartFromStorage();
    this.syncTicketSelectorsInFrame(this.currentTicketsFrame());
  };

  isOnTicketSelectionPage() {
    return /^\/events\/[^/]+\/tickets$/.test(window.location.pathname);
  }

  currentTicketsFrame() {
    return (
      this.element.querySelector('turbo-frame#tickets') ||
      this.document.querySelector('turbo-frame#tickets')
    );
  }

  beforeTicketsFrameRender = (event) => {
    const frame = this.ticketsFrameFromEvent(event);
    if (!frame) return;

    this.renderCartFromStorage();
  };

  seedEventTicketIdsFromPage() {
    const fromParent = TicketCartStorage.parseVisibleTicketsJson(
      this.element.getAttribute('data-ticket-selection-visible-tickets') ||
        this.element.dataset.ticketSelectionVisibleTickets,
    );
    const fromContainer = this.visibleTicketIdsFromContainer();

    const ids = TicketCartStorage.preferredVisibleTicketIdList(
      fromParent,
      fromContainer,
      TicketCartStorage.readCart(this.eventSlug).eventTicketIds,
    );

    if (ids?.length) TicketCartStorage.mergeEventTicketIds(this.eventSlug, ids);
  }

  visibleTicketIdsFromContainer(frame = null) {
    const container = this.ticketsContainerElement(frame);
    if (!container?.dataset.visibleTickets) return [];

    return TicketCartStorage.parseVisibleTicketsJson(
      container.dataset.visibleTickets,
    );
  }

  preserveCartOnCategorySwitch() {
    this.renderCartFromStorage();
  }

  beforeTicketsFrameFetch = (event) => {
    const turboFrame =
      event.detail?.fetchOptions?.headers?.['Turbo-Frame'] ||
      event.detail?.fetchOptions?.headers?.get?.('Turbo-Frame');

    if (turboFrame !== 'tickets') return;

    this.renderCartFromStorage();
  };

  onTicketsFrameEvent = (event) => {
    const frame = this.ticketsFrameFromEvent(event);
    if (!frame) return;

    this.afterTicketsFrameUpdated(frame);
    requestAnimationFrame(() => this.afterTicketsFrameUpdated(frame));
  };

  afterTicketsFrameUpdated(frame) {
    const frameIds = this.visibleTicketIdsFromContainer(frame);
    if (frameIds.length) {
      TicketCartStorage.mergeEventTicketIds(this.eventSlug, frameIds);
    }

    this.syncTicketSelectorsInFrame(frame);
    this.renderCartFromStorage();
  }

  ticketsFrameFromEvent(event) {
    if (event.target?.id === 'tickets') return event.target;
    return event.target?.closest?.('turbo-frame#tickets') || null;
  }

  ticketsContainerElement(frame = null) {
    return (
      frame?.querySelector?.('.tickets-container') ||
      this.element.querySelector('.tickets-container') ||
      document.querySelector('.tickets-container')
    );
  }

  ticketSelectorChange() {
    this.renderCartFromStorage();
    this.hideSeatingInfo();
  }

  renderCartFromStorage() {
    this.updateCartDisplay();
    this.enableOrDisableBuyButton();
    this.showOrHideOrderLimitError();
    this.syncCartDrawerVisibility();
    this.dispatchTicketQuantityChanged();
  }

  dispatchTicketQuantityChanged() {
    const detail = {
      totalQuantity: this.totalQuantity,
      alreadyHasTickets: this.alreadyHasTickets,
      ecommerceData: this.safeCartEcommerceData(),
    };

    this.element.dispatchEvent(
      new CustomEvent('howler:ticket-quantity-changed', {
        bubbles: true,
        detail,
      }),
    );
    this.document.dispatchEvent(
      new CustomEvent('howler:ticket-quantity-changed', { detail }),
    );
  }

  syncCartDrawerVisibility() {
    const cartDrawer = this.element.querySelector(
      '[data-controller~="cart-drawer"]',
    );
    if (!cartDrawer) return;

    const showDrawer = this.totalQuantity > 0 || this.alreadyHasTickets;

    cartDrawer.classList.toggle('cart-drawer--hidden', !showDrawer);

    const cartDetails = cartDrawer.querySelector(
      '[data-target="cart-drawer.cartDetails"]',
    );
    if (!cartDetails) return;

    if (this.alreadyHasTickets && this.totalQuantity <= 0) {
      cartDetails.classList.add('cart-drawer__details--hidden');
    } else {
      cartDetails.classList.remove('cart-drawer__details--hidden');
    }
  }

  syncTicketSelectorsInFrame(frame) {
    const root = frame || this.element;
    if (!root?.querySelectorAll) return;

    root
      .querySelectorAll('[data-controller~="ticket-selector"]')
      .forEach((ticketElement) => {
        try {
          const ticketId = JSON.parse(
            ticketElement.getAttribute('ecommerce-data'),
          ).items[0].item_id;
          const saved = TicketCartStorage.getTicket(this.eventSlug, ticketId);
          if (!saved) return;

          const quantity = Number(saved.ticketSelectorQuantity || 0);
          const display = ticketElement.querySelector(
            '[data-target="ticket-selector.display"]',
          );
          if (display) display.textContent = `${quantity}`;
          ticketElement.classList.toggle('is-selected', quantity > 0);
        } catch (error) {
          console.error(
            'Failed to sync ticket selector from cart storage',
            error,
          );
        }
      });
  }

  safeCartEcommerceData() {
    try {
      return this.cartEcommerceData;
    } catch (error) {
      console.error('Failed to build cart ecommerce data', error);
      return {
        currency:
          this.ticketTypeTargets[0]?.dataset.ticketSelectorCurrency || '',
        value: this.grandTotal,
        items: [],
      };
    }
  }

  updateCartDisplay() {
    const totalQuantity = this.totalQuantity;
    this.cartCountDisplayTargets.forEach(
      (t) => (t.textContent = `${totalQuantity}`),
    );
    this.cartCountWordsDisplayTargets.forEach((t) => {
      t.textContent =
        totalQuantity > 1
          ? this.ticketCounterTranslationsHash.other.replace(
              '%{count}',
              totalQuantity,
            )
          : this.ticketCounterTranslationsHash.one.replace(
              '%{count}',
              totalQuantity,
            );
    });
    this.cartTicketCostDisplayTargets.forEach(
      (t) =>
        (t.textContent = `${this.currencySymbol} ${this.totalTicketCost.toFixed(
          2,
        )}`),
    );
    this.cartFeeDisplayTargets.forEach(
      (t) =>
        (t.textContent = `${this.currencySymbol} ${this.totalFees.toFixed(2)}`),
    );
    this.cartTotalDisplayTargets.forEach(
      (t) =>
        (t.textContent = `${this.currencySymbol} ${this.grandTotal.toFixed(
          2,
        )}`),
    );
  }

  enableOrDisableBuyButton() {
    const hasTickets = this.totalQuantity > 0;
    const disabled = !hasTickets && !this.alreadyHasTickets;

    if (disabled) {
      this.cartTargets.forEach((t) => t.classList.add('is-empty'));
      this.document.documentElement.classList.remove('ticket-cart-is-open');
    } else {
      this.cartTargets.forEach((t) => t.classList.remove('is-empty'));
      this.document.documentElement.classList.add('ticket-cart-is-open');
    }

    this.checkoutButtonTargets.forEach((button) => {
      button.disabled = disabled;
    });
  }

  showOrHideOrderLimitError() {
    const maxOrderLimitReached = this.maxOrderLimitReached;
    const ticketTypeCount = this.ticketTypeTargets.length;
    if (
      this._maxOrderLimitReached === maxOrderLimitReached &&
      this._ticketTypeCount === ticketTypeCount
    ) {
      return;
    }

    this._maxOrderLimitReached = maxOrderLimitReached;
    this._ticketTypeCount = ticketTypeCount;

    if (maxOrderLimitReached) {
      this.maxErrorMessageTargets.forEach((t) =>
        t.classList.remove('util-hidden'),
      );
      this.ticketTypeTargets.forEach((tt) => {
        if (tt.dataset.ticketSelectorMaxOrderLimitReached !== 'true') {
          tt.dataset.ticketSelectorMaxOrderLimitReached = 'true';
        }
      });
      this.document.documentElement.classList.add('ticket-cart-is-maxed');
    } else {
      this.maxErrorMessageTargets.forEach((t) =>
        t.classList.add('util-hidden'),
      );
      this.ticketTypeTargets.forEach((tt) => {
        if (tt.dataset.ticketSelectorMaxOrderLimitReached !== 'false') {
          tt.dataset.ticketSelectorMaxOrderLimitReached = 'false';
        }
      });
      this.document.documentElement.classList.remove('ticket-cart-is-maxed');
    }
  }

  hideSeatingInfo() {
    this.seatingInfoTargets.forEach((t) => t.classList.add('util-hidden'));
    this.checkoutButtonTargets.forEach((button) =>
      button.classList.remove('util-hidden'),
    );
  }

  get ticketsInCart() {
    return this.allSavedTickets;
  }

  get cartEcommerceData() {
    const currency =
      this.ticketTypeTargets[0]?.dataset.ticketSelectorCurrency || '';
    const ecommerceData = { currency, value: this.grandTotal };
    ecommerceData.items = this.ticketsInCart.flatMap((ticket) => {
      const items = ticket?.ticketSelectorEcommerceData?.items;
      if (!items?.length) return [];

      return items.map((item) => ({
        ...item,
        quantity: ticket.ticketSelectorQuantity,
      }));
    });
    return ecommerceData;
  }

  trackCartEvent(eventName) {
    try {
      const ecommerceData = this.cartEcommerceData;

      if (!ecommerceData.event_slug) {
        ecommerceData.event_slug =
          this.eventSlug || window.eventSlug || window.persistedEventSlug;
      }
      ecommerceData.event_slug =
        this.eventSlug || window.eventSlug || window.persistedEventSlug;

      if (ecommerceData.event_slug && !ecommerceData['epn.event_slug']) {
        ecommerceData['epn.event_slug'] = ecommerceData.event_slug;
      }
      trackGa4Event(eventName, ecommerceData);
    } catch (err) {
      console.error(err);
    }
  }

  trackViewItemListEvent() {
    const items = this.allSavedTickets.flatMap(
      (ticket) => ticket?.ticketSelectorEcommerceData?.items || [],
    );
    const eventSlug =
      this.eventSlug ||
      window.eventSlug ||
      window.persistedEventSlug ||
      this.getEventSlugFromURL();
    trackGa4Event('view_item_list', {
      event_slug: eventSlug,
      'epn.event_slug': eventSlug,
      item_list_id: `${eventSlug}_tickets`,
      item_list_name: `${this.eventName} - tickets`,
      items,
    });
  }

  getEventSlugFromURL() {
    return this.eventSlugFromURL();
  }

  checkout() {
    this.beforeSubmitForm();

    this.element.classList.add('contents-loading');
    this.trackCartEvent('begin_checkout');

    if (this.totalQuantity <= 0 && this.alreadyHasTickets) {
      this.continueFormTarget.submit();
    } else {
      this.orderFormTarget.submit();
    }
  }

  checkoutSeated() {
    this.element.classList.add('contents-loading');
  }

  toggleCartDrawer() {
    this.cartDrawerTarget.classList.toggle('is-open');
    this.document.documentElement.classList.toggle('ticket-cart-is-expanded');

    if (this.cartDrawerTarget.classList.contains('is-open'))
      this.trackCartEvent('view_cart');
  }

  removeHtmlClasses = () => {
    this.document.documentElement.classList.remove('ticket-cart-is-expanded');
    this.document.documentElement.classList.remove('ticket-cart-is-open');
    this.document.documentElement.classList.remove('ticket-cart-is-maxed');
  };

  removeElementContentLoading = () => {
    this.element.classList.remove('contents-loading');
  };

  sumTicketSelectorAttribute(attr) {
    return TicketCartStorage.sumTicketAttribute(this.eventSlug, attr);
  }

  beforeSubmitForm() {
    const ticketOrderForm = this.orderFormTarget;
    const validTicketIds = new Set(this.validTicketIds());

    this.allSavedTickets.forEach((ticket) => {
      const itemId = Number(
        ticket?.ticketSelectorEcommerceData?.items?.[0]?.item_id,
      );
      if (!itemId || !validTicketIds.has(itemId)) return;

      const inputName = `ticket_order[ticket_type_ids][${itemId}][amount]`;
      let input = ticketOrderForm.querySelector(`input[name="${inputName}"]`);

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = inputName;
        ticketOrderForm.appendChild(input);
      }

      input.value = ticket.ticketSelectorQuantity;
    });

    this.removeUnchangedTicketTypeFields(ticketOrderForm);
  }

  // Large events render one ticket_order[ticket_type_ids][id][amount] hidden
  // field per ticket type across every category (see TicketTypeComponent),
  // which can push the request past Rack's query parameter limit. Only a
  // field whose value differs from what the server originally rendered
  // represents an actual change, so only those need to reach the server.
  //
  // input.defaultValue can't tell us that: type="hidden" has no dirty-value
  // flag, so .value and .defaultValue always stay equal to each other, no
  // matter what JS assigns to .value. TicketTypeComponent stamps the
  // originally-rendered amount into data-original-amount instead, which JS
  // mutation never touches, so we diff against that snapshot (missing on a
  // field created fresh by the loop above for a cross-category ticket -
  // treated as "originally 0", which always keeps that field since it's only
  // ever created for a currently non-zero cart quantity).
  removeUnchangedTicketTypeFields(form) {
    form
      .querySelectorAll('input[name^="ticket_order[ticket_type_ids]"]')
      .forEach((input) => {
        const originalAmount = Number(input.dataset.originalAmount || 0);
        if (Number(input.value) === originalAmount) input.remove();
      });
  }

  validTicketIds() {
    const cart = TicketCartStorage.readCart(this.eventSlug);
    const ids = new Set(cart.eventTicketIds.map((id) => Number(id)));

    this.allSavedTickets.forEach((ticket) => {
      const itemId = ticket?.ticketSelectorEcommerceData?.items?.[0]?.item_id;
      if (itemId != null) ids.add(Number(itemId));
    });

    return Array.from(ids);
  }
}
